import React, { useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import {
  XMarkIcon,
  ChevronDownIcon,
  UserCircleIcon,
  PlusIcon,
} from "@heroicons/react/24/outline";
// MODIFICATION: Import Redux hooks and the actions from your chat slice
import { setDataVersion } from "./ChatSlice_Store"; // Adjust path if necessary
// Import typed Redux hooks
import { useAppDispatch, useAppSelector } from "./hooks/reduxHooks";
import LogoutButton from "./components/auth/LogoutButton";
import { useAuthIntegration } from "./hooks/useAuthIntegration";
import { selectCurrentProject } from "./ProjectSlice_Store";
import { startNewProject } from "./ChatSlice_Store";
import ProjectSummaryModal from "./components/ProjectSummaryModal_Component";
import ChatContextMenu from "./components/ChatContextMenu_Component";
import RenameChatModal from "./components/RenameChatModal_Component";
import DeleteChatModal from "./components/DeleteChatModal_Component";

interface HeaderComponentProps {
  onToggleSidebar: () => void;
  isMobileSidebarOpen: boolean;
  onOpenWelcomeModal?: () => void;
}

export default function Header_Component({ onToggleSidebar, isMobileSidebarOpen, onOpenWelcomeModal }: HeaderComponentProps): JSX.Element {
  // Set up Redux dispatch and selector
  const dispatch = useAppDispatch();
  const { userName, userEmail, isAuthenticated } = useAppSelector((state) => state.userAuthAndAdminSlice);
  const currentProject = useAppSelector(selectCurrentProject);

  // State for user dropdown
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState<boolean>(false);
  const userDropdownRef = useRef<HTMLDivElement>(null);

  // State for project summary modal
  const [isProjectModalOpen, setIsProjectModalOpen] = useState<boolean>(false);

  // State for header context menu
  const [isHeaderMenuOpen, setIsHeaderMenuOpen] = useState<boolean>(false);
  const [headerMenuPosition, setHeaderMenuPosition] = useState<{ top: number; left: number }>({ top: 0, left: 0 });

  // State for rename modal (triggered from header context menu)
  const [isRenameModalOpen, setIsRenameModalOpen] = useState<boolean>(false);

  // State for delete modal — capture target so modal survives currentProject clearing
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);

  // State for project name animation
  const [projectNameKey, setProjectNameKey] = useState<number>(0);
  const prevProjectNameRef = useRef<string | undefined>(currentProject?.name);

  // Initialize auth integration
  useAuthIntegration();

  // Ensure data version is always set to 2 (Public)
  useEffect(() => {
    dispatch(setDataVersion(2));
  }, [dispatch]);

  // Effect to handle clicks outside the user dropdown to close it
  useEffect(() => {
    function handleClickOutside(event: MouseEvent): void {
      if (
        userDropdownRef.current &&
        !userDropdownRef.current.contains(event.target as Node)
      ) {
        setIsUserDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Close header context menu when active project changes
  useEffect(() => {
    setIsHeaderMenuOpen(false);
  }, [currentProject?.id]);

  // Effect to trigger animation when project name changes
  useEffect(() => {
    const newName = currentProject?.name;
    if (prevProjectNameRef.current !== newName && newName) {
      setProjectNameKey(prev => prev + 1); // Force re-render with animation
      prevProjectNameRef.current = newName;
    }
  }, [currentProject?.name]);

  return (
    <header className="bg-white border-b border-gray-200">
      <div className="relative flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4">
        {/* Left section - Menu button, New button, and Project Name */}
        <div className="flex items-center">
          {/* Menu button - visible on mobile + tablet, hidden on desktop */}
          <button
            onClick={onToggleSidebar}
            className="desktop:hidden p-2 rounded-lg hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 mr-2"
            aria-label="Toggle navigation menu"
          >
            {isMobileSidebarOpen ? (
              <XMarkIcon className="h-6 w-6 text-gray-600" />
            ) : (
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M2.25 9C2.25 8.85082 2.30926 8.70774 2.41475 8.60225C2.52024 8.49676 2.66332 8.4375 2.8125 8.4375L15.1875 8.4375C15.3367 8.4375 15.4798 8.49676 15.5852 8.60225C15.6907 8.70774 15.75 8.85082 15.75 9C15.75 9.14919 15.6907 9.29226 15.5852 9.39775C15.4798 9.50324 15.3367 9.5625 15.1875 9.5625L2.8125 9.5625C2.66332 9.5625 2.52024 9.50324 2.41475 9.39775C2.30926 9.29226 2.25 9.14918 2.25 9ZM15.1875 12.9375L2.8125 12.9375C2.66332 12.9375 2.52024 12.9968 2.41475 13.1023C2.30926 13.2077 2.25 13.3508 2.25 13.5C2.25 13.6492 2.30926 13.7923 2.41475 13.8977C2.52024 14.0032 2.66332 14.0625 2.8125 14.0625L15.1875 14.0625C15.3367 14.0625 15.4798 14.0032 15.5852 13.8977C15.6907 13.7923 15.75 13.6492 15.75 13.5C15.75 13.3508 15.6907 13.2077 15.5852 13.1023C15.4798 12.9968 15.3367 12.9375 15.1875 12.9375ZM2.8125 5.0625L15.1875 5.0625C15.3367 5.0625 15.4798 5.00324 15.5852 4.89775C15.6907 4.79226 15.75 4.64919 15.75 4.5C15.75 4.35082 15.6907 4.20774 15.5852 4.10225C15.4798 3.99676 15.3367 3.9375 15.1875 3.9375L2.8125 3.9375C2.66332 3.9375 2.52024 3.99676 2.41475 4.10225C2.30926 4.20774 2.25 4.35082 2.25 4.5C2.25 4.64918 2.30926 4.79226 2.41475 4.89775C2.52024 5.00324 2.66332 5.0625 2.8125 5.0625Z" fill="#234E81"/>
              </svg>
            )}
          </button>

          {/* Tablet-only "New" button */}
          <button
            onClick={() => dispatch(startNewProject())}
            className="hidden tablet:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-cvc-blue text-cvc-blue text-sm font-medium hover:bg-cvc-blue/5 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors duration-200 mr-3"
            aria-label="New chat"
          >
            <PlusIcon className="h-4 w-4" />
            New
          </button>

          {/* Project Name (non-tablet, non-mobile) - clickable to show summary with fade-in animation */}
          {currentProject?.name && (
            <div className="hidden sm:flex items-center gap-2 tablet:hidden">
              <button
                key={projectNameKey}
                onClick={() => setIsProjectModalOpen(true)}
                className="text-sm sm:text-base font-medium hover:opacity-80 hover:underline focus:outline-none focus:underline transition-colors duration-200 animate-fade-in"
                style={{ color: "#234E81" }}
                aria-label="View project summary"
              >
                {currentProject.name}
              </button>

              {/* Chevron dropdown for context menu */}
              <div className="relative">
                <button
                  onClick={(e) => {
                    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                    setHeaderMenuPosition({ top: rect.bottom + 4, left: rect.left });
                    setIsHeaderMenuOpen(!isHeaderMenuOpen);
                  }}
                  className="p-1 rounded hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors duration-200"
                  aria-label="Chat options"
                  title="Chat options"
                >
                  <ChevronDownIcon
                    className={`h-4 w-4 transition-transform duration-200 ${isHeaderMenuOpen ? 'rotate-180' : ''}`}
                    style={{ color: "#234E81" }}
                  />
                </button>

              </div>
            </div>
          )}
        </div>

        {/* Tablet: absolutely centered project name — independent of left/right section widths */}
        {currentProject?.name ? (
          <div className="hidden tablet:flex items-center justify-center gap-2 absolute left-1/2 -translate-x-1/2 max-w-[40%]">
            <button
              key={projectNameKey}
              onClick={() => setIsProjectModalOpen(true)}
              className="text-sm sm:text-base font-medium hover:opacity-80 hover:underline focus:outline-none focus:underline transition-colors duration-200 animate-fade-in truncate"
              style={{ color: "#234E81" }}
              aria-label="View project summary"
            >
              {currentProject.name}
            </button>

            <button
              onClick={(e) => {
                const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                setHeaderMenuPosition({ top: rect.bottom + 4, left: rect.left });
                setIsHeaderMenuOpen(!isHeaderMenuOpen);
              }}
              className="p-1 flex-shrink-0 rounded hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors duration-200"
              aria-label="Chat options"
              title="Chat options"
            >
              <ChevronDownIcon
                className={`h-4 w-4 transition-transform duration-200 ${isHeaderMenuOpen ? 'rotate-180' : ''}`}
                style={{ color: "#234E81" }}
              />
            </button>
          </div>
        ) : null}
        {/* Context menu — portaled to body to avoid position:fixed breakage under tablet transform */}
        {isHeaderMenuOpen && currentProject?.id && createPortal(
          <ChatContextMenu
            isOpen={true}
            onClose={() => setIsHeaderMenuOpen(false)}
            position={headerMenuPosition}
            onRename={() => {
              setIsHeaderMenuOpen(false);
              setIsRenameModalOpen(true);
            }}
            onDelete={() => {
              setIsHeaderMenuOpen(false);
              if (currentProject?.id) {
                setDeleteTarget({
                  id: currentProject.id,
                  name: currentProject.name || `Project ${currentProject.id.slice(0, 8)}`,
                });
              }
            }}
          />,
          document.body
        )}

        {/* Right section - About DealOS + User Info */}
        <div className="flex items-center gap-3 sm:gap-4">
          {/* About DealOS | BETA button */}
          {onOpenWelcomeModal && (
            <button
              type="button"
              onClick={onOpenWelcomeModal}
              className="flex items-center gap-1 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors duration-200"
              aria-label="About DealOS"
            >
              <span
                className="text-xs font-cvcBody px-2.5 py-1 rounded-l-full"
                style={{ color: "#234E81", backgroundColor: "rgba(35,78,129,0.1)" }}
              >
                About DealOS
              </span>
              <span
                className="text-xs font-cvcBody px-2.5 py-1 rounded-r-full"
                style={{ color: "#009174", backgroundColor: "rgba(0,145,116,0.2)" }}
              >
                BETA
              </span>
            </button>
          )}

          {/* User Info Section */}
          {isAuthenticated && (
            <div className="relative" ref={userDropdownRef}>
              <button
                onClick={() => setIsUserDropdownOpen(!isUserDropdownOpen)}
                className="flex items-center space-x-2 p-2 rounded-lg hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors duration-200"
                aria-label="User menu"
              >
                <UserCircleIcon className="h-7 w-7" style={{ color: "#234E81" }} />
                <ChevronDownIcon
                  className={`h-4 w-4 transition-transform duration-200 ${
                    isUserDropdownOpen ? "rotate-180" : ""
                  }`}
                  style={{ color: "#234E81" }}
                />
              </button>

              {/* User Dropdown Menu */}
              {isUserDropdownOpen && (
                <div className="absolute right-0 mt-2 w-64 origin-top-right bg-white rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-50">
                  <div className="p-4 border-b border-gray-200">
                    <div className="flex items-center space-x-3">
                      <UserCircleIcon className="h-10 w-10 text-gray-400" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {userName || "User"}
                        </p>
                        <p className="text-sm text-gray-500 truncate">
                          {userEmail || "No email"}
                        </p>
                      </div>
                    </div>
                  </div>
                  {/* Feature flag toggles */}
                  <div className="px-4 py-3 border-b border-gray-200">
                    <label className="flex items-center justify-between cursor-pointer">
                      <span className="text-xs text-gray-600">Inline Deals</span>
                      <button
                        type="button"
                        role="switch"
                        aria-checked={localStorage.getItem("ff_inlineDeals") === "true"}
                        onClick={() => {
                          const current = localStorage.getItem("ff_inlineDeals") === "true";
                          localStorage.setItem("ff_inlineDeals", current ? "false" : "true");
                          window.location.reload();
                        }}
                        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                          localStorage.getItem("ff_inlineDeals") === "true" ? "bg-cvc-blue" : "bg-gray-300"
                        }`}
                      >
                        <span
                          className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${
                            localStorage.getItem("ff_inlineDeals") === "true" ? "translate-x-4" : "translate-x-0.5"
                          }`}
                        />
                      </button>
                    </label>
                  </div>
                  <div className="py-1">
                    <div className="px-4 py-2">
                      <LogoutButton />
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Project Summary Modal */}
      <ProjectSummaryModal
        isOpen={isProjectModalOpen}
        onClose={() => setIsProjectModalOpen(false)}
        projectName={currentProject?.name}
        projectSummary={currentProject?.summary}
        projectId={currentProject?.id}
        onRename={() => {
          setIsProjectModalOpen(false);
          setIsRenameModalOpen(true);
        }}
        onDelete={() => {
          setIsProjectModalOpen(false);
          if (currentProject?.id) {
            setDeleteTarget({
              id: currentProject.id,
              name: currentProject.name || `Project ${currentProject.id.slice(0, 8)}`,
            });
          }
        }}
      />

      {/* Rename Chat Modal */}
      {currentProject?.id && (
        <RenameChatModal
          isOpen={isRenameModalOpen}
          onClose={() => setIsRenameModalOpen(false)}
          projectId={currentProject.id}
          currentName={currentProject.name || ''}
        />
      )}

      {/* Mobile: full-width project name bar */}
      {currentProject?.name && (
        <div className="sm:hidden flex items-center gap-2 px-4 pb-2 bg-white">
          <button
            key={projectNameKey}
            onClick={() => setIsProjectModalOpen(true)}
            className="text-sm font-medium hover:opacity-80 hover:underline focus:outline-none focus:underline transition-colors duration-200 animate-fade-in truncate"
            style={{ color: "#234E81" }}
            aria-label="View project summary"
          >
            {currentProject.name}
          </button>
          <button
            onClick={(e) => {
              const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
              setHeaderMenuPosition({ top: rect.bottom + 4, left: rect.left });
              setIsHeaderMenuOpen(!isHeaderMenuOpen);
            }}
            className="p-1 flex-shrink-0 rounded hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors duration-200"
            aria-label="Chat options"
            title="Chat options"
          >
            <ChevronDownIcon
              className={`h-4 w-4 transition-transform duration-200 ${isHeaderMenuOpen ? 'rotate-180' : ''}`}
              style={{ color: "#234E81" }}
            />
          </button>
        </div>
      )}

      {/* Delete Chat Modal — rendered from captured target, not currentProject */}
      {deleteTarget && (
        <DeleteChatModal
          isOpen={true}
          onClose={() => setDeleteTarget(null)}
          projectId={deleteTarget.id}
          projectName={deleteTarget.name}
          onDeleted={() => {
            setDeleteTarget(null);
            dispatch(startNewProject());
          }}
        />
      )}
    </header>
  );
}
